import React, { FC, memo, useCallback, useState, useMemo } from 'react';
import classNames from 'classnames';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { ConfirmModal } from 'shared/ui/ConfirmModal';
import { Modal } from 'shared/ui/Modal';
import { Button } from 'shared/ui/Button';
import { Table } from 'shared/ui/Table';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { TableActions, TableRowActions } from 'shared/ui/Table/types';
import { ReceiptDocument, ReceiptStatus, useLazyGetReceiptsByPaymentIdQuery, useIssueReceiptMutation } from 'entities/Receipt';
import { ReactComponent as PlusIcon } from 'shared/assets/icons/PlusIcon.svg';
import { ReactComponent as DocumentIcon } from 'shared/assets/icons/DocumentIcon.svg';

interface ReceiptActionsProps {
  paymentId: string;
  isReceiptIssued: boolean;
}

export const ReceiptActions: FC<ReceiptActionsProps> = memo(({ paymentId, isReceiptIssued }) => {
  const { t } = useAppTranslation(['contracts', 'common', 'payment']);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [fetchReceipts, { data: receipts }] = useLazyGetReceiptsByPaymentIdQuery();
  const [issueReceipt, { isLoading: isReceiptIssuing }] = useIssueReceiptMutation();

  const [isOpenedConfirm, setOpenedConfirm] = useState(false);
  const openConfirmModal = useCallback((): void => {
    setOpenedConfirm(true);
  }, []);

  const closeConfirmModal = useCallback((): void => {
    setOpenedConfirm(false);
  }, []);

  const openModal = useCallback(async (): Promise<void> => {
    try {
      setIsModalOpen(true);
      await fetchReceipts(paymentId).unwrap();
    } catch (error: CustomAny) {
      showNotification('error', t('Error', { ns: 'common' }), t('Failed to load receipts'));
    }
  }, [fetchReceipts, paymentId, t]);

  const closeModal = useCallback((): void => {
    setIsModalOpen(false);
  }, []);

  const columns = [
    { title: t('Processor', { ns: 'payment' }), dataIndex: 'processor', key: 'processor' },
    { title: t('Status', { ns: 'payment' }), dataIndex: 'status', key: 'status', render: (val: string) => t(val, { ns: 'payment' }) },
    {
      title: t('Created', { ns: 'payment' }),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (val: string) => getClientDateFormat(val),
    },
    {
      title: t('Error'),
      dataIndex: 'error',
      key: 'error',
      render: (val: CustomAny) => val?.text || val || '-',
      width: '30%',
    },
    {
      title: '',
      dataIndex: 'receiptUrl',
      key: 'receiptUrl',
      render: (val: string) => {
        if (val) {
          return (
            <div className="cursor-pointer flex items-center justify-center" onClick={() => window.open(val, '_blank')}>
              <DocumentIcon /> <span className="ml-2 text-success">{t('Show receipt', { ns: 'payment' })}</span>
            </div>
          );
        }

        return null;
      },
    },
  ];

  const { isDesktop } = useScreenBreakpoints();

  const tableActions: Array<TableActions<ReceiptDocument>> = useMemo(
    () => [
      {
        name: t('Issue receipt', { ns: 'payment' }),
        icon: <PlusIcon />,
        iconPosition: 'prev',
        onClick: () => {
          openConfirmModal();
        },
      },
    ],
    [openConfirmModal, t],
  );

  const handleIssueReceipt = useCallback(async (): Promise<void> => {
    try {
      await issueReceipt({ paymentId }).unwrap();

      showNotification('info', t('Success', { ns: 'common' }), t('Issue receipt request created', { ns: 'common' }));
    } catch (error: CustomAny) {
      console.log(error);
      showNotification('error', t('Error', { ns: 'common' }), t('Error when sending issue receipt request', { ns: 'common' }));
    } finally {
      closeConfirmModal();
    }
  }, [paymentId, t, issueReceipt, closeConfirmModal]);

  const rowActions: Array<TableRowActions<ReceiptDocument>> = []; // useMemo(
  //   () => [
  //     {
  //       name: t('Show receipt'),
  //       icon: <DocumentIcon />,
  //       theme: 'clear',
  //       description: t('Show receipt'),
  //       onClick: (receipt: ReceiptDocument) => {
  //         console.log(receipt);
  //       },
  //     },
  //   ],
  //   [t, openConfirmModal],
  // );

  const highlightRow = (record: ReceiptDocument): string => {
    return classNames({
      'bg-errorLight': record.status === ReceiptStatus.FAILED,
      'bg-successLight': record.status === ReceiptStatus.DONE,
      'bg-primaryLight': record.status === ReceiptStatus.PENDING,
    });
  };

  return (
    <>
      <Button theme="outlined" onClick={openModal}>
        {t('История чеков')}
      </Button>

      <Modal isOpen={isModalOpen} onClose={closeModal} width={isDesktop ? 980 : 380}>
        <Table
          tableActionsPosition="prev"
          data={receipts || []}
          columns={columns}
          rowKey="receiptId"
          size="small"
          tableActions={tableActions}
          rowActions={rowActions}
          rowClassName={highlightRow}
        />

        <ConfirmModal
          isOpened={isOpenedConfirm}
          title={t('Issue receipt?', { ns: 'common' })}
          description={t('Are you sure you want to issue receipt for this payment?', { ns: 'common' })}
          isLoading={isReceiptIssuing}
          onOk={handleIssueReceipt}
          onCancel={closeConfirmModal}
        />
      </Modal>
    </>
  );
});
